import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProductsModel } from '../models/Products';
import { useProducts } from './useProducts';
import { useQueryParams } from '@shared/hooks/useQueryParams';

type SortingState = { field: string; direction: 'asc' | 'desc' } | null;

const INITIAL_SELECTED_COLUMNS = [
  'name',
  'code',
  'cost',
  'stock.disponible',
  'stock.reservado',
  'stock.total',
  'created_at',
  'client',
  'actions',
] as const;

const FIXED_COLUMNS = ['name', 'code'] as const;

const COLUMN_MAP: Record<string, string> = {
  code: 'code',
  cost: 'cost',
  'stock.disponible': 'available',
  'stock.reservado': 'reserved',
  'stock.total': 'stock',
  team_id: 'team_id',
  category_id: 'category_id',
  created_at: 'created_at',
  name: 'name',
};

const FIELD_TO_API_SORT: Record<string, string> = {
  name: 'name',
  code: 'code',
  cost: 'cost',
  'stock.disponible': 'available',
  'stock.reservado': 'reserved',
  'stock.total': 'stock',
  created_at: 'created_at',
  client: 'team_id',
};

const STOCK_TYPE_MAP: Record<string, number> = {
  'stock.disponible': 1,
  'stock.reservado': 2,
  'stock.entrante': 1,
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value as FilterConditionOutput[];
};

const computeAdvancedFilters = (
  conditions: FilterConditionOutput[],
  searchValue: string,
): { filters: any[]; logic: 'AND' | 'OR' } | null => {
  if (!conditions || conditions.length === 0) {
    return searchValue
      ? {
          filters: [
            { column: 'name', operator: 'contains', value: searchValue },
            { column: 'code', operator: 'contains', value: searchValue },
          ],
          logic: 'OR',
        }
      : null;
  }

  const stockFilter = conditions.find((condition) => STOCK_TYPE_MAP[condition.column] !== undefined);

  if (stockFilter) {
    const mappedType = STOCK_TYPE_MAP[stockFilter.column];
    const quantityValue = Array.isArray(stockFilter.value) ? stockFilter.value : [stockFilter.value];

    return {
      filters: [
        {
          column: 'type_id',
          operator: 'in',
          value: [mappedType],
        },
        {
          column: 'quantity',
          operator: stockFilter.action,
          value: quantityValue,
        },
      ],
      logic: 'AND',
    };
  }

  const mappedFilters = conditions
    .map((condition) => {
      const apiColumn = COLUMN_MAP[condition.column];
      if (!apiColumn) {
        return null;
      }

      const requiresArray = ['in', 'not_in'].includes(condition.action);
      const isNullOperator = ['is_null', 'is_not_null'].includes(condition.action);

      const value = isNullOperator
        ? undefined
        : requiresArray
          ? Array.isArray(condition.value)
            ? condition.value
            : [condition.value]
          : condition.value;

      const filter: Record<string, unknown> = {
        column: apiColumn,
        operator: condition.action,
      };

      if (value !== undefined) {
        filter.value = value;
      }

      return filter;
    })
    .filter(Boolean) as any[];

  if (mappedFilters.length > 0) {
    return {
      filters: mappedFilters,
      logic: 'AND',
    };
  }

  return searchValue
    ? {
        filters: [
          { column: 'name', operator: 'contains', value: searchValue },
          { column: 'code', operator: 'contains', value: searchValue },
        ],
        logic: 'OR',
      }
    : null;
};

export const useProductsMain = () => {
  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'id' as string },
    sortDirection: { type: 'string', default: 'desc' as string },
  });

  const initialConditions = parseFiltersFromParams(queryParams.filters);

  const [searchTerm, setSearchTerm] = useState<string>((queryParams.search ?? '').trim());
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(initialConditions);
  const filterConditionsSerializedRef = useRef<string>(serializeConditions(initialConditions));
  const [sorting, setSorting] = useState<SortingState>(() => {
    const sortField = queryParams.sortBy || 'id';
    const direction = queryParams.sortDirection === 'asc' ? 'asc' : 'desc';
    return {
      field: sortField,
      direction,
    };
  });
  const [selectedColumns, setSelectedColumns] = useState<string[]>([...INITIAL_SELECTED_COLUMNS]);
  const [cachedProducts, setCachedProducts] = useState<ProductsModel[]>([]);
  const [cachedTotal, setCachedTotal] = useState(0);

  useEffect(() => {
    const paramSearch = (queryParams.search ?? '').trim();
    setSearchTerm((prev) => (prev === paramSearch ? prev : paramSearch));
  }, [queryParams.search]);

  useEffect(() => {
    const nextConditions = parseFiltersFromParams(queryParams.filters);
    const nextSerialized = serializeConditions(nextConditions);

    if (nextSerialized !== filterConditionsSerializedRef.current) {
      filterConditionsSerializedRef.current = nextSerialized;
      setFilterConditions(nextConditions);
    }
  }, [queryParams.filters]);

  useEffect(() => {
    const sortField = queryParams.sortBy || 'id';
    const direction = queryParams.sortDirection === 'asc' ? 'asc' : 'desc';

    setSorting((prev) => {
      if (prev && prev.field === sortField && prev.direction === direction) {
        return prev;
      }

      return {
        field: sortField,
        direction,
      };
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  const advancedFilters = useMemo(
    () => computeAdvancedFilters(filterConditions, searchTerm),
    [filterConditions, searchTerm],
  );

  const processedFilters = useMemo(() => {
    const apiField = sorting?.field ? FIELD_TO_API_SORT[sorting.field] || sorting.field : 'id';
    const apiDirection = sorting?.direction ?? 'desc';

    return {
      sortBy: apiField,
      sortDirection: apiDirection,
    };
  }, [sorting]);

  const queryFilters = useMemo(() => {
    const baseFilters: Record<string, unknown> = {
      ...processedFilters,
      ...(advancedFilters ? { advanced_filters: advancedFilters } : {}),
    };

    if (searchTerm) {
      baseFilters.searchTerm = searchTerm;
    }

    return baseFilters;
  }, [processedFilters, advancedFilters, searchTerm]);

  const {
    products: fetchedProducts,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total: fetchedTotal,
  } = useProducts(queryFilters);

  useEffect(() => {
    if (fetchedProducts.length > 0) {
      setCachedProducts(fetchedProducts);
      setCachedTotal(fetchedTotal);
    }
  }, [fetchedProducts.length, fetchedTotal]);

  const handleSearch = useCallback(
    (term: string) => {
      const trimmedTerm = term.trim();
      setSearchTerm(trimmedTerm);
      updateParam('search', trimmedTerm);
    },
    [searchTerm, updateParam],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      const serialized = serializeConditions(conditions);

      if (filterConditionsSerializedRef.current === serialized) {
        return;
      }

      filterConditionsSerializedRef.current = serialized;
      setFilterConditions(conditions);
      updateParam('filters', conditions);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((columns: string[]) => {
    setSelectedColumns(columns);
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setSorting(newSorting);
      if (newSorting && newSorting.field) {
        updateParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      } else {
        updateParams({
          sortBy: '',
          sortDirection: '',
        });
      }
    },
    [updateParams],
  );

  // 🎯 Detectar si hay filtros activos (excluyendo sorting por defecto)
  // El sorting por defecto no se considera un filtro activo
  const hasActiveFilters = useMemo(
    () => filterConditions.length > 0 || Boolean(searchTerm.trim()),
    [filterConditions.length, searchTerm],
  );

  const shouldUseCachedData = !hasActiveFilters && fetchedProducts.length === 0 && cachedTotal > 0;

  const products = shouldUseCachedData ? cachedProducts : fetchedProducts;
  const total = shouldUseCachedData ? cachedTotal : fetchedTotal;

  const productsViewProps = useMemo(
    () => ({
      products,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
    }),
    [products, fetchNextPage, hasNextPage, isFetchingNextPage, total],
  );

  const exportColumns = useMemo(
    () => selectedColumns.map((columnId) => COLUMN_MAP[columnId]).filter(Boolean) as string[],
    [selectedColumns],
  );

  return {
    products,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    sorting,
    selectedColumns,
    searchTerm,
    productsViewProps,
    exportColumns,
    hasActiveFilters,
    fixedColumns: [...FIXED_COLUMNS],
    filterConditions,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
  };
};
